/**
 * Segmento (Imóvel/Móvel) de uma venda.
 *
 * POR QUE ESTE ARQUIVO EXISTE: o segmento NÃO mora na venda. Nem
 * `servopa_vendas` nem a view `servopa_vendas_with_details` têm a coluna —
 * as duas só carregam `group_number`. Quem sabe se o grupo é de imóvel ou de
 * móvel é `servopa_groups.segment` ('I'/'M'). É a mesma travessia que as
 * Comissões já fazem para ratear repasse por segmento.
 *
 * Como o card (rótulo) e a aba (filtro) precisam da MESMA tradução grupo →
 * segmento, ela mora aqui, sem React: assim dá pra conferir a regra sem
 * renderizar tela, que é onde ela é fácil de quebrar sem ninguém ver.
 */

export type Segmento = "I" | "M";

/** "todos" é o estado neutro do filtro: não restringe nada. */
export type FiltroSegmento = "todos" | Segmento;

/** Linha de `servopa_groups` — só o que interessa pra resolver o segmento. */
export interface GrupoComSegmento {
  group_number: string | number | null;
  segment: string | null;
}

/**
 * Rótulo de UI. Venda sem grupo (ou com grupo que ainda não sincronizou para
 * `servopa_groups`) cai no travessão, o mesmo vazio dos outros campos do card
 * — dizer "Imóvel" por omissão seria pior do que não dizer nada.
 */
export const rotuloSegmento = (segmento: Segmento | null | undefined): string =>
  segmento === "I" ? "Imóvel" : segmento === "M" ? "Móvel" : "-";

/**
 * Número do grupo como chave de mapa.
 *
 * Existe porque os dois lados guardam o grupo em tipos diferentes: na venda é
 * TEXT, em `servopa_groups` é INTEGER. Sem passar os dois pela mesma peneira,
 * o mapa nunca casa e o card mostra "-" para tudo.
 */
export const chaveDoGrupo = (grupo: string | number | null | undefined): string | null => {
  if (grupo === null || grupo === undefined) return null;
  const texto = String(grupo).trim();
  return texto === "" ? null : texto;
};

/** Só 'I' e 'M' são segmentos; qualquer outro valor da base vira "sem segmento". */
const segmentoValido = (valor: string | null | undefined): Segmento | null =>
  valor === "I" || valor === "M" ? valor : null;

/** Mapa grupo → segmento, a partir das linhas de `servopa_groups`. */
export const mapaSegmentoPorGrupo = (grupos: GrupoComSegmento[] | null | undefined): Map<string, Segmento> => {
  const mapa = new Map<string, Segmento>();
  for (const grupo of grupos ?? []) {
    const chave = chaveDoGrupo(grupo?.group_number);
    const segmento = segmentoValido(grupo?.segment);
    if (chave && segmento) mapa.set(chave, segmento);
  }
  return mapa;
};

/** Segmento de uma venda, pelo grupo dela. */
export const segmentoDaVenda = (
  grupoDaVenda: string | number | null | undefined,
  mapa: Map<string, Segmento>,
): Segmento | null => {
  const chave = chaveDoGrupo(grupoDaVenda);
  return chave ? mapa.get(chave) ?? null : null;
};

/** Grupos de um segmento, em ordem estável — é o que vira filtro no servidor. */
export const gruposDoSegmento = (mapa: Map<string, Segmento>, segmento: Segmento): string[] =>
  [...mapa.entries()]
    .filter(([, seg]) => seg === segmento)
    .map(([grupo]) => grupo)
    .sort();

/**
 * Fragmento de filtro da listagem de vendas.
 *
 * O filtro tem que ser do SERVIDOR, não da página exibida: a lista é paginada
 * de 25 em 25, então peneirar no cliente esconderia parte do resultado e ainda
 * mostraria um total errado. Como a view não tem `segment`, traduzimos o
 * segmento na lista de grupos dele e filtramos por grupo.
 *
 * `carregado` distingue "ainda não sei os grupos" de "não existe grupo neste
 * segmento": no primeiro caso não filtramos nada (a lista aparece inteira por
 * um instante), no segundo mandamos a lista vazia, que o dataProvider trata
 * como "nenhuma venda" — o certo, e não "todas".
 */
export const filtroDeSegmento = (
  filtro: FiltroSegmento,
  mapa: Map<string, Segmento>,
  carregado: boolean,
): Record<string, string[]> => {
  if (filtro === "todos" || !carregado) return {};
  return { group_numbers: gruposDoSegmento(mapa, filtro) };
};
